import React from 'react'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { useNetWorthStore, useScreenSize, useWidgetContainerProvider } from '../../common'
import { LineChart } from '../../common/components/charts/LineChart'

import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'

const NetWorthChart = () => {
  const { isMobile } = useScreenSize()
  const { availableWidth } = useWidgetContainerProvider()
  const theme = useTheme()
  const {
    hoveredData,
    netWorthChartData: chartData,
    selectedData,
    setHoveredIndex,
    setSelectedIndex,
  } = useNetWorthStore()

  // on mobile, chart is same width as list; on desktop, chart takes up what the list does not
  const chartWidthNotMobile = availableWidth - 320

  const datasets = chartData.map((point) => {
    return {
      x: formatDate(fromUnixTime(point.x), DATE_FORMATS_INTL.MONTH_SHORT),
      y: point.y,
    }
  })

  const selectedMark = selectedData
    ? { index: chartData.findIndex((point) => point.x === selectedData.x) }
    : undefined

  const hoveredMark = hoveredData
    ? { index: chartData.findIndex((point) => point.x === hoveredData.x) }
    : undefined

  const getMarkStyles = (markIndex: number) => {
    return {
      fill: markIndex === selectedMark?.index ? theme.palette.success.main : '',
      filter:
        markIndex === hoveredMark?.index
          ? `drop-shadow(0px 0px 10px ${theme.palette.success.main})`
          : 'none',
      stroke: theme.palette.success.main,
    }
  }

  const onMarkClick = (_event: React.MouseEvent<SVGPathElement, MouseEvent>, dataIndex: number) => {
    setSelectedIndex(dataIndex)
  }

  const onMouseEnter = (dataIndex: number) => {
    setHoveredIndex(dataIndex)
  }

  const onMouseLeave = () => {
    setHoveredIndex(-1)
  }

  return (
    <Stack sx={{ width: '100%' }}>
      <LineChart
        baseline="min" // causes the area below the line to be shaded
        chartFor={'networthChart'}
        chartXaxisSx={{ '& .MuiChartsAxis-tickLabel': { transform: 'translateX(8px)' } }}
        chartYaxisSx={{ '& .MuiChartsAxis-tickLabel': { transform: 'translateX(-8px)' } }}
        colors={[theme.palette.success.main]}
        datasets={[datasets]}
        height={350}
        labels={[]}
        margin={{ right: 20, left: 38 }}
        markHandlers={{ onMarkClick, onMouseEnter, onMouseLeave }}
        markStyles={getMarkStyles}
        showArea={true}
        useCustomMark={true}
        valueFormatterString="0.0[0]a" // formatting example -'109,000' -> '109k', 1,092,742 → $1.1m, $1,236 → $1.2k
        width={isMobile ? availableWidth - 48 : chartWidthNotMobile}
      />
    </Stack>
  )
}

export default observer(NetWorthChart)
